import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import { 
    collection, 
    query, 
    where, 
    onSnapshot, 
    orderBy,
    updateDoc,
    doc,
    arrayUnion,
    serverTimestamp
} from 'firebase/firestore';
import {
    Briefcase,
    MapPin,
    Calendar,
    Clock,
    DollarSign,
    AlertCircle,
    Star,
    Zap,
    ThumbsUp,
    Loader
} from 'lucide-react';

export default function AvailableJobs() {
    const { currentUser, userProfile } = useAuth();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expressingInterest, setExpressingInterest] = useState({});

    // Real-time listener for available jobs
    useEffect(() => {
        // Query for jobs that are:
        // 1. Visible to contractors (visibleToContractors = true)
        // 2. Not yet assigned (contractorId is null or empty)
        // 3. Status is 'assigned' (ready to be picked up)
        
        const q = query(
            collection(db, 'workOrders'),
            where('visibleToContractors', '==', true),
            where('status', '==', 'assigned'),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const availableJobs = snapshot.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .filter(job => !job.contractorId); // Additional client-side filter

            setJobs(availableJobs);
            setLoading(false);
        }, (error) => {
            console.error('Error fetching available jobs:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handleExpressInterest = async (jobId) => {
        setExpressingInterest(prev => ({ ...prev, [jobId]: true }));

        try {
            const jobRef = doc(db, 'workOrders', jobId);
            
            // Add contractor to interested contractors array
            await updateDoc(jobRef, {
                interestedContractors: arrayUnion({
                    contractorId: currentUser.uid,
                    contractorName: userProfile?.name || 'Unknown',
                    contractorEmail: currentUser.email,
                    expressedAt: new Date().toISOString()
                }),
                updatedAt: new Date().toISOString()
            });

            // Success message (you can add toast notification here)
            alert('Interest registered! Admin will review and may assign you to this job.');

        } catch (error) {
            console.error('Error expressing interest:', error);
            alert('Failed to express interest. Please try again.');
        } finally {
            setExpressingInterest(prev => ({ ...prev, [jobId]: false }));
        }
    };

    const hasExpressedInterest = (job) => {
        if (!job.interestedContractors) return false;
        return job.interestedContractors.some(
            contractor => contractor.contractorId === currentUser.uid
        );
    };

    const getUrgencyBadge = (urgency) => {
        const badges = {
            'standard': { label: 'Standard', color: 'bg-blue-100 text-blue-800', icon: Clock },
            'urgent': { label: 'Urgent', color: 'bg-orange-100 text-orange-800', icon: Zap },
            'emergency': { label: 'Emergency', color: 'bg-red-100 text-red-800', icon: AlertCircle }
        };
        return badges[urgency] || badges['standard'];
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading available jobs...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">
                        Available Jobs
                    </h1>
                    <p className="text-gray-600 mt-2">
                        Browse open work orders and express your interest
                    </p>
                </div>

                {/* Job Count */}
                <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2">
                        <Briefcase className="w-5 h-5 text-blue-600" />
                        <span className="text-blue-900 font-medium">
                            {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'} available
                        </span>
                    </div>
                </div>

                {/* Jobs Grid */}
                {jobs.length > 0 ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {jobs.map((job, index) => {
                            const urgencyBadge = getUrgencyBadge(job.urgency);
                            const UrgencyIcon = urgencyBadge.icon;
                            const alreadyInterested = hasExpressedInterest(job);

                            return (
                                <motion.div
                                    key={job.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                                >
                                    {/* Header */}
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex-1">
                                            <h3 className="text-lg font-bold text-gray-900 mb-2">
                                                {job.serviceType}
                                            </h3>
                                            <div className="flex items-center space-x-2">
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${urgencyBadge.color} flex items-center space-x-1`}>
                                                    <UrgencyIcon className="w-3 h-3" />
                                                    <span>{urgencyBadge.label}</span>
                                                </span>
                                                {job.interestedContractors?.length > 0 && (
                                                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                        {job.interestedContractors.length} interested
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Job Details */}
                                    <div className="space-y-3 mb-4">
                                        <div className="flex items-start space-x-2 text-sm text-gray-600">
                                            <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                            <span>{job.location}</span>
                                        </div>

                                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                                            <Calendar className="w-4 h-4 flex-shrink-0" />
                                            <span>
                                                Preferred: {new Date(job.preferredDate).toLocaleDateString()}
                                            </span>
                                        </div>

                                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                                            <Clock className="w-4 h-4 flex-shrink-0" />
                                            <span>Est. {job.estimatedHours} hours</span>
                                        </div>

                                        {job.totalPrice && (
                                            <div className="flex items-center space-x-2 text-sm font-semibold text-green-600">
                                                <DollarSign className="w-4 h-4 flex-shrink-0" />
                                                <span>Job Value: ${job.totalPrice.toFixed(2)}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Description */}
                                    {job.description && (
                                        <div className="mb-4">
                                            <p className="text-sm text-gray-600 line-clamp-2">
                                                {job.description}
                                            </p>
                                        </div>
                                    )}

                                    {/* Client Info (Optional) */}
                                    {job.clientName && (
                                        <div className="mb-4 text-sm text-gray-600">
                                            <span className="font-medium">Client:</span> {job.clientName}
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
                                        {alreadyInterested ? (
                                            <div className="flex-1 bg-green-50 border border-green-200 text-green-800 px-4 py-2 rounded-lg text-center font-medium flex items-center justify-center space-x-2">
                                                <ThumbsUp className="w-4 h-4" />
                                                <span>Interest Registered</span>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => handleExpressInterest(job.id)}
                                                disabled={expressingInterest[job.id]}
                                                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center space-x-2 disabled:opacity-50"
                                            >
                                                {expressingInterest[job.id] ? (
                                                    <>
                                                        <Loader className="w-4 h-4 animate-spin" />
                                                        <span>Submitting...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Star className="w-4 h-4" />
                                                        <span>Express Interest</span>
                                                    </>
                                                )}
                                            </button>
                                        )}

                                        <Link
                                            to={`/contractor/available-jobs/${job.id}`}
                                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                        >
                                            View Details
                                        </Link>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                        <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            No Available Jobs
                        </h3>
                        <p className="text-gray-600">
                            Check back later for new work opportunities.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
